import { useContext, useEffect, useState } from "react";

import { calculate_cecl_from_stresstest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/cecl";
import { calculateStressTest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/crop/crop_stress_test";
import { ApiHelper, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { InsuranceService } from "./InsuranceService";

export default function useInsurance({
  insurance_scenario_id,
  state_name,
  county_name,
  commodity_name,
  type_name,
  practice_name,

  crop_year,
  skipStressTest = false,
  skipInsurancePlans = false,
}) {
  const { globalState } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();

  // --- --- ---

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [insurancePlanTimestamp, setInsurancePlanTimestamp] = useState(uuidv4());
  const [insurancePlans, setInsurancePlans] = useState([]);

  // --- --- ---

  const [insuranceScenarios, setInsuranceScenarios] = useState(null);
  const [isLoadingInsuranceScenarios, setIsLoadingInsuranceScenarios] = useState(true);
  const loadInsuranceScenario = async () => {
    try {
      setIsLoadingInsuranceScenarios(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: crop_year,
        is_active: true,
      };
      if (insurance_scenario_id) {
        producerFilter.id = insurance_scenario_id;
      }
      const _insuranceScenarios = await UsersApiHelper.users_selectObject(
        "InsuranceScenario",
        producerFilter,
        globalState
      );

      setInsuranceScenarios(_insuranceScenarios);

      setIsLoadingInsuranceScenarios(false);
    } catch (err) {
      setIsLoadingInsuranceScenarios(false);
      setInsuranceScenarios([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.producer_token) {
      loadInsuranceScenario();
    }
  }, [globalState?.producer_token]);

  // --- --- ---

  const [budgetCountyPractices, setBudgetCountyPractices] = useState(null);
  const [isLoadingBudgetCountyPractices, setIsLoadingBudgetCountyPractices] = useState(true);
  const loadBudgetCountyPractices = async () => {
    try {
      const _budgetFilter = {
        // state_name, county_name,
        // commodity_name, type_name, practice_name,
        year: crop_year,
        status: BUDGET_STATUS_ACTIVE,
        is_active: true,
      };
      if (state_name) {
        _budgetFilter.state_name = state_name;
      }
      if (county_name) {
        _budgetFilter.county_name = county_name;
      }
      if (commodity_name) {
        _budgetFilter.commodity_name = commodity_name;
      }
      if (type_name) {
        _budgetFilter.type_name = type_name;
      }
      if (practice_name) {
        _budgetFilter.practice_name = practice_name;
      }

      const _budgets = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);

      setBudgetCountyPractices(_budgets);

      // const _cropFilter = {
      //     crop_year: crop_year,
      //     location_state_name: state_name, location_county_name: county_name,
      //     commodity_name, type_name, practice_name,
      //     is_active: true,
      // };
      // const _crops = await UsersApiHelper.selectProducerFarmFieldCrops(_cropFilter, globalState);
      // const _cropsCountyPractice = getUniqueCounties(_crops);

      // if (_budgets?.length) {
      //     for (const _activeBudgetCountyPractice of _budgets) {
      //         _activeBudgetCountyPractice.reinsurance_year = _activeBudgetCountyPractice?.year;

      //         _activeBudgetCountyPractice.production_cost_per_acre = _activeBudgetCountyPractice?.reported_acreage > 0 &&
      //             _activeBudgetCountyPractice?.production_cost / _activeBudgetCountyPractice?.reported_acreage;

      //         // if (_cropsCountyPractice?.length) {
      //         //     const _currentCropCountyPractice = _cropsCountyPractice[0];

      //         //     _activeBudgetCountyPractice.approved_yield = _currentCropCountyPractice.approved_yield_weighted;
      //         //     _activeBudgetCountyPractice.rate_yield = _currentCropCountyPractice.rate_yield_weighted;
      //         //     _activeBudgetCountyPractice.adjusted_yield = _currentCropCountyPractice.adjusted_yield_weighted;
      //         //     _activeBudgetCountyPractice.share_percent = _currentCropCountyPractice.share_percent_weighted;
      //         // }
      //     }

      //     setBudgetCountyPractices(_activeBudgetCountyPractice);
      // }

      setIsLoadingBudgetCountyPractices(false);
    } catch (err) {
      setIsLoadingBudgetCountyPractices(false);
      setBudgetCountyPractices(null);
      handleError(err);
    }
  };

  useEffect(() => {
    // if (insuranceScenarios?.length) {
    loadBudgetCountyPractices();
    // }
  }, [globalState?.producer_token]);

  // --- --- ---

  const [producerFarmFieldCrops, setProducerFarmFieldCrops] = useState(null);
  const loadProducerFarmFieldCrops = async () => {
    const _cropFilter = {
      crop_year: crop_year,
      // location_state_name: state_name,
      // location_county_name: county_name,
      // commodity_name, type_name, practice_name,
      is_active: true,
    };
    if (state_name) {
      _cropFilter.location_state_name = state_name;
    }
    if (county_name) {
      _cropFilter.location_county_name = county_name;
    }
    if (commodity_name) {
      _cropFilter.commodity_name = commodity_name;
    }
    if (type_name) {
      _cropFilter.type_name = type_name;
    }
    if (practice_name) {
      _cropFilter.practice_name = practice_name;
    }

    const _crops = await UsersApiHelper.selectProducerFarmFieldCrops(_cropFilter, globalState);

    if (_crops?.length) {
      setProducerFarmFieldCrops(_crops);
    }
  };

  useEffect(() => {
    if (budgetCountyPractices?.length) {
      loadProducerFarmFieldCrops();
    }
  }, [globalState?.producer_token, budgetCountyPractices]);

  // --- --- ---

  const _loadCountyData_Intern = async (_currentCountyFilter) => {
    const currentPrices = await RmaApiHelper.selectPrices(_currentCountyFilter);
    const currentCountyData = await RmaApiHelper.selectCountyData(_currentCountyFilter);
    const currentCounty = {
      ...(currentPrices && currentPrices.length && currentPrices[0]),
      ..._currentCountyFilter,
      reinsurance_year: crop_year,
      expected_index_value: currentCountyData?.length && currentCountyData[0].expected_index_value,
    };
    return currentCounty;
  };

  // County data is loaded on demand via _loadCountyData_Intern, per budget county practice.

  // --- --- ---

  const [isLoadingInsurancePlans, setIsLoadingInsurancePlans] = useState(true);
  const loadInsurancePlans = async () => {
    try {
      setIsLoadingInsurancePlans(true);
      setInsurancePlanTimestamp(uuidv4());

      const _InsuranceService = new InsuranceService({
        // insuranceScenarios: insuranceScenarios.filter(isc => isc),
        // budgetCountyPractices: budgetCountyPractices.filter(cp => cp),
        crop_year: crop_year,
        apiConfig: globalState,
      });
      const _insuranceScenarioPopulated = await _InsuranceService.actions.loadInsuranceScenario();

      if (
        _insuranceScenarioPopulated?.length &&
        _insuranceScenarioPopulated[0].budgetCountyPractices?.length &&
        _insuranceScenarioPopulated[0].budgetCountyPractices[0]?.insurancePlans?.length
      ) {
        const _allInsurancePlans = [];
        for (const _currentBudgetCountyPractice of _insuranceScenarioPopulated[0].budgetCountyPractices) {
          _allInsurancePlans.push(..._currentBudgetCountyPractice.insurancePlans);
        }

        setInsurancePlans(_allInsurancePlans);
        setInsurancePlanTimestamp(uuidv4());

        _InsuranceService.actions
          .calculatePremiums(_insuranceScenarioPopulated[0])
          .then((calculatePremiumsResponseInsuranceScenario) => {
            const _allCalculatedInsurancePlans = [];
            for (const _currentBudgetCountyPractice of calculatePremiumsResponseInsuranceScenario?.budgetCountyPractices) {
              _allCalculatedInsurancePlans.push(..._currentBudgetCountyPractice.insurancePlans);
            }

            setInsurancePlans(_allCalculatedInsurancePlans);
            setInsurancePlanTimestamp(uuidv4());
            setIsLoadingInsurancePlans(false);
          });
      } else {
        setInsurancePlans([]);
        setInsurancePlanTimestamp(uuidv4());
        setIsLoadingInsurancePlans(false);
      }
    } catch (err) {
      setIsLoadingInsurancePlans(false);
      setInsurancePlans(null);
      setInsurancePlanTimestamp(uuidv4());
      handleError(err);
    }
  };

  useEffect(() => {
    if (skipInsurancePlans) {
      setIsLoadingInsurancePlans(false);
      return;
    }
    if (globalState?.producer_token && insuranceScenarios?.length && budgetCountyPractices?.length) {
      loadInsurancePlans();
    } else if (!globalState?.producer_token || !budgetCountyPractices?.length) {
      setInsurancePlans([]);
      setIsLoadingInsurancePlans(false);
    }
  }, [globalState?.producer_token, budgetCountyPractices, insuranceScenarios, skipInsurancePlans]);

  // --- --- ---

  const [stressTestParameters, setStressTestParameters] = useState(null);
  const [stressTestResults, setStressTestResults] = useState(null);
  const [isLoadingStressTestResults, setIsLoadingStressTestResults] = useState(true);
  const loadStressTestResults = async () => {
    try {
      let _currentCountyFilter = {
        reinsurance_year: crop_year,
        state_name,
        county_name,
        commodity_name,
        type_name,
        practice_name,
      };

      const _currentStressTestResults = [];
      // Loop in a way that supports await budgetCountyPractice
      for (const _currentBudgetCountyPractice of budgetCountyPractices) {
        _currentCountyFilter.state_name = _currentBudgetCountyPractice.state_name;
        _currentCountyFilter.county_name = _currentBudgetCountyPractice.county_name;
        _currentCountyFilter.commodity_name = _currentBudgetCountyPractice.commodity_name;
        _currentCountyFilter.type_name = _currentBudgetCountyPractice.type_name;
        _currentCountyFilter.practice_name = _currentBudgetCountyPractice.practice_name;
        const _currentCountyData = await _loadCountyData_Intern(_currentCountyFilter);

        const props = {
          farmParameters: _currentBudgetCountyPractice,
          countyParameters: [_currentCountyData],
          insurancePlanParameters: insurancePlans,
          testParameters: stressTestParameters,
          apiHelper: ApiHelper,
        };

        // console.log("stressTest props", props);
        const _currentStressTestResults_Batch = await calculateStressTest(props);
        if (_currentStressTestResults_Batch) {
          _currentStressTestResults_Batch.cecl = calculate_cecl_from_stresstest({
            stressTestResults: _currentStressTestResults.results,
          });

          _currentStressTestResults.push(_currentStressTestResults_Batch);

          // console.log("stressTest results", _currentStressTestResults);
          // console.log("cecl", _currentStressTestResults?.cecl);
        }
      }

      setStressTestResults(_currentStressTestResults);
      setIsLoadingStressTestResults(false);
    } catch (err) {
      setIsLoadingStressTestResults(false);
      setStressTestResults([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (
      skipStressTest ||
      !(
        globalState?.producer_token &&
        budgetCountyPractices?.length /*&& countyData?.length*/ &&
        insurancePlans?.length &&
        stressTestParameters != null
      )
    ) {
      return;
    }
    loadStressTestResults();
  }, [
    skipStressTest,
    globalState?.producer_token,
    budgetCountyPractices,
    /*countyData,*/ isLoadingInsurancePlans,
    insurancePlanTimestamp,
    stressTestParameters,
  ]);

  // ---

  const loadStressTestParameters = async () => {
    const filter = {
      producer_token: globalState.producer_token,
      // insurance_scenario_id,
      // state_name,
      // county_name,
      // commodity_name,
      // type_name,
      // practice_name,
      is_active: true,
    };
    if (insurance_scenario_id) filter.insurance_scenario_id = insurance_scenario_id;
    if (state_name) {
      filter.state_name = state_name;
    }
    if (county_name) {
      filter.county_name = county_name;
    }
    if (commodity_name) {
      filter.commodity_name = commodity_name;
    }
    if (type_name) {
      filter.type_name = type_name;
    }
    if (practice_name) {
      filter.practice_name = practice_name;
    }

    const _stressTestParameters = await UsersApiHelper.users_selectObject(
      "InsuranceStressTestParameters",
      filter,
      globalState
    );
    setStressTestParameters(_stressTestParameters);
  };

  useEffect(() => {
    if (skipStressTest || !globalState?.producer_token) {
      return;
    }
    loadStressTestParameters();
  }, [skipStressTest, globalState?.producer_token]);

  // --- --- ---

  return {
    budgetCountyPractices,
    isLoadingBudgetCountyPractices,

    producerFarmFieldCrops,

    insuranceScenarios,
    isLoadingInsuranceScenarios,
    insurancePlans,
    isLoadingInsurancePlans,

    stressTestParameters,
    stressTestResults,
    isLoadingStressTestResults,
  };
}
